import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Wallet, CheckCircle2, XCircle, AlertCircle, Loader2, RefreshCw,
  Link2, Unlink, DollarSign, TrendingUp, CreditCard, Receipt,
  ExternalLink, Clock, User, Zap, Filter,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

interface MPStatus {
  conectado: boolean
  nickname?: string
  email?: string
  user_id?: number
  ambiente?: string
  conta_bancaria_id?: number
  ultimo_sync?: string
}

interface MPTransacao {
  id: number
  mp_payment_id: number
  status: string
  status_detail: string
  payment_method: string
  valor_bruto: number
  valor_liquido: number
  taxa_mp: number
  data_aprovacao?: string
  data_criacao?: string
  descricao?: string
  external_reference?: string
  pedido_id?: number
  payer_nome?: string
  payer_email?: string
  parcelas: number
  conciliado: boolean
}

const STATUS_META: Record<string, { label: string; cor: string; icon: any }> = {
  approved: { label: 'Aprovado', cor: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  pending: { label: 'Pendente', cor: 'bg-amber-100 text-amber-700', icon: Clock },
  in_process: { label: 'Em análise', cor: 'bg-blue-100 text-blue-700', icon: Clock },
  rejected: { label: 'Rejeitado', cor: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelado', cor: 'bg-ink-100 text-ink-700', icon: XCircle },
  refunded: { label: 'Reembolsado', cor: 'bg-purple-100 text-purple-700', icon: RefreshCw },
  charged_back: { label: 'Chargeback', cor: 'bg-red-200 text-red-900', icon: AlertCircle },
}

const METHOD_LABELS: Record<string, string> = {
  pix: 'PIX',
  credit_card: 'Cartão',
  debit_card: 'Débito',
  visa: 'Visa',
  master: 'Master',
  elo: 'Elo',
  account_money: 'Saldo MP',
  ticket: 'Boleto',
}

export default function MercadoPagoPage() {
  const qc = useQueryClient()
  const [mostrarConectar, setMostrarConectar] = useState(false)
  const [token, setToken] = useState('')
  const [ambiente, setAmbiente] = useState<'producao' | 'teste'>('producao')
  const [contaBancariaId, setContaBancariaId] = useState<number | ''>('')
  const [filtroStatus, setFiltroStatus] = useState<string>('')

  const { data: status, refetch: refetchStatus } = useQuery<MPStatus>({
    queryKey: ['mp-status'],
    queryFn: async () => (await api.get('/mp/status')).data,
  })

  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const { data: transacoes = [], refetch: refetchTrans, isLoading: loadingTrans } = useQuery<MPTransacao[]>({
    queryKey: ['mp-transacoes', filtroStatus],
    queryFn: async () => (await api.get('/mp/transacoes', { params: { status: filtroStatus || undefined } })).data,
    enabled: !!status?.conectado,
  })

  const { data: resumo } = useQuery<any>({
    queryKey: ['mp-resumo'],
    queryFn: async () => (await api.get('/mp/resumo')).data,
    enabled: !!status?.conectado,
  })

  const conectar = useMutation({
    mutationFn: async () => api.post('/mp/conectar', {
      access_token: token,
      ambiente,
      conta_bancaria_id: contaBancariaId || null,
    }),
    onSuccess: (r: any) => {
      toast.success(r.data.mensagem || 'Conectado!')
      setMostrarConectar(false)
      setToken('')
      refetchStatus()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao conectar'),
  })

  const desconectar = useMutation({
    mutationFn: async () => api.post('/mp/desconectar'),
    onSuccess: () => { toast.success('Desconectado'); refetchStatus() },
  })

  const sincronizar = useMutation({
    mutationFn: async () => api.post('/mp/sincronizar', { dias: 30 }),
    onSuccess: (r: any) => {
      toast.success(r.data.mensagem)
      refetchStatus(); refetchTrans()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const conciliar = useMutation({
    mutationFn: async (id: number) => api.post('/mp/conciliar', { transacao_id: id }),
    onSuccess: () => { toast.success('Transação conciliada'); refetchTrans() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const statusTransacoes = ['approved', 'pending', 'in_process', 'rejected', 'refunded']

  return (
    <div className="p-4 lg:p-6 max-w-[1500px] mx-auto">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
          <Wallet size={22} className="text-blue-500" /> Mercado Pago
        </h1>
        <p className="text-sm text-ink-500">
          Integração com Mercado Pago: recebimentos, PIX, cartão, boleto e conciliação automática.
        </p>
      </div>

      {/* CARD CONEXAO */}
      <div className="card p-4 mb-4">
        {!status?.conectado ? (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                <Unlink size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-ink-900">Não conectado</h3>
                <p className="text-xs text-ink-500">Conecte sua conta Mercado Pago para sincronizar pagamentos automaticamente.</p>
              </div>
              <button
                onClick={() => setMostrarConectar(!mostrarConectar)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-lg flex items-center gap-2 hover:shadow-md">
                <Link2 size={16} /> Conectar
              </button>
            </div>

            {mostrarConectar && (
              <div className="mt-4 pt-4 border-t space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">📋 Como obter seu Access Token:</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Acesse <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer" className="underline font-semibold">developers.mercadopago.com.br</a></li>
                    <li>Crie uma aplicação (ou use uma existente)</li>
                    <li>Em "Credenciais", copie o <strong>Access Token</strong> de produção</li>
                    <li>Cole no campo abaixo</li>
                  </ol>
                </div>

                <div>
                  <label className="label">Access Token</label>
                  <input
                    type="password"
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    placeholder="APP_USR-xxxxxxxx-xxxxxx-xxx..."
                    className="input font-mono text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Ambiente</label>
                    <select value={ambiente} onChange={e => setAmbiente(e.target.value as any)} className="input">
                      <option value="producao">Produção</option>
                      <option value="teste">Teste / Sandbox</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Conta bancária (onde entra o recebimento)</label>
                    <select value={contaBancariaId} onChange={e => setContaBancariaId(Number(e.target.value) || '')} className="input">
                      <option value="">— Opcional —</option>
                      {contas.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    disabled={!token || conectar.isPending}
                    onClick={() => conectar.mutate()}
                    className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg disabled:opacity-50">
                    {conectar.isPending ? <Loader2 className="inline animate-spin" size={14} /> : 'Conectar'}
                  </button>
                  <button onClick={() => setMostrarConectar(false)} className="px-4 py-2 text-ink-600 text-sm">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <CheckCircle2 size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-ink-900 flex items-center gap-2">
                  {status.nickname || status.email}
                  {status.ambiente === 'teste' && (
                    <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded font-bold">SANDBOX</span>
                  )}
                </h3>
                <p className="text-xs text-ink-500">
                  {status.email} · User #{status.user_id}
                  {status.ultimo_sync && ` · Último sync: ${new Date(status.ultimo_sync).toLocaleString('pt-BR')}`}
                </p>
              </div>
              <button
                onClick={() => sincronizar.mutate()}
                disabled={sincronizar.isPending}
                className="px-3 py-2 bg-brand-500 text-white font-semibold rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">
                {sincronizar.isPending ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                Sincronizar
              </button>
              <button
                onClick={() => { if (confirm('Desconectar do Mercado Pago?')) desconectar.mutate() }}
                className="p-2 text-ink-500 hover:bg-red-50 hover:text-red-600 rounded-lg">
                <Unlink size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {status?.conectado && (
        <>
          {/* RESUMO */}
          {resumo?.por_status && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              {statusTransacoes.map(s => {
                const m = STATUS_META[s]
                const dados = resumo.por_status[s] || { qtd: 0, bruto: 0, liquido: 0, taxa: 0 }
                const Icon = m?.icon || Receipt
                return (
                  <div key={s} className="card p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <Icon size={12} className="text-ink-400" />
                      <p className="text-[10px] uppercase tracking-wider text-ink-500">{m?.label || s}</p>
                    </div>
                    <p className="text-xl font-bold text-ink-900">{formatMoney(dados.bruto)}</p>
                    <p className="text-xs text-ink-500">{dados.qtd} transação{dados.qtd !== 1 ? 'ões' : ''}</p>
                  </div>
                )
              })}
            </div>
          )}

          {/* FILTROS */}
          <div className="flex items-center gap-2 mb-3">
            <Filter size={14} className="text-ink-400" />
            <button
              onClick={() => setFiltroStatus('')}
              className={`px-3 py-1 rounded text-xs ${!filtroStatus ? 'bg-brand-500 text-white' : 'bg-white border border-ink-200'}`}>
              Todos
            </button>
            {statusTransacoes.map(s => {
              const m = STATUS_META[s]
              return (
                <button key={s}
                  onClick={() => setFiltroStatus(s)}
                  className={`px-3 py-1 rounded text-xs ${filtroStatus === s ? 'bg-brand-500 text-white' : 'bg-white border border-ink-200'}`}>
                  {m?.label || s}
                </button>
              )
            })}
          </div>

          {/* LISTA TRANSACOES */}
          <div className="card overflow-hidden">
            {loadingTrans ? (
              <div className="p-8 text-center text-ink-400">
                <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                Carregando transações...
              </div>
            ) : transacoes.length === 0 ? (
              <div className="p-8 text-center text-ink-400">
                <Receipt size={36} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">Nenhuma transação ainda</p>
                <p className="text-xs mt-1">Clique em "Sincronizar" para buscar pagamentos do Mercado Pago</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-xs text-ink-600">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Pagador</th>
                    <th className="text-left p-2">Método</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-right p-2">Bruto</th>
                    <th className="text-right p-2">Taxa</th>
                    <th className="text-right p-2">Líquido</th>
                    <th className="text-center p-2">Conc.</th>
                    <th className="p-2 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {transacoes.map(t => {
                    const m = STATUS_META[t.status] || { label: t.status, cor: 'bg-ink-100 text-ink-700', icon: AlertCircle }
                    return (
                      <tr key={t.id} className={t.conciliado ? 'bg-emerald-50/30' : ''}>
                        <td className="p-2 text-xs">
                          {t.data_aprovacao && new Date(t.data_aprovacao).toLocaleDateString('pt-BR')}
                          <p className="text-[10px] text-ink-400">
                            {t.data_aprovacao && new Date(t.data_aprovacao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </td>
                        <td className="p-2">
                          <p className="font-medium truncate max-w-[180px]">{t.payer_nome || '—'}</p>
                          <p className="text-[10px] text-ink-500 truncate max-w-[180px]">{t.payer_email}</p>
                        </td>
                        <td className="p-2 text-xs">
                          <span className="bg-ink-100 text-ink-700 px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold">
                            {METHOD_LABELS[t.payment_method] || t.payment_method}
                          </span>
                          {t.parcelas > 1 && <span className="text-[10px] text-ink-500 ml-1">{t.parcelas}x</span>}
                        </td>
                        <td className="p-2 text-center">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${m.cor}`}>
                            {m.label}
                          </span>
                        </td>
                        <td className="p-2 text-right font-mono text-sm">{formatMoney(t.valor_bruto)}</td>
                        <td className="p-2 text-right font-mono text-xs text-red-600">
                          {t.taxa_mp > 0 ? `−${formatMoney(t.taxa_mp)}` : '—'}
                        </td>
                        <td className="p-2 text-right font-mono text-sm font-semibold text-emerald-700">
                          {formatMoney(t.valor_liquido || t.valor_bruto)}
                        </td>
                        <td className="p-2 text-center">
                          {t.conciliado
                            ? <CheckCircle2 size={14} className="text-emerald-600 mx-auto" />
                            : <span className="text-ink-300">—</span>}
                        </td>
                        <td className="p-2">
                          {!t.conciliado && t.status === 'approved' && (
                            <button
                              onClick={() => conciliar.mutate(t.id)}
                              disabled={conciliar.isPending}
                              className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-semibold hover:bg-emerald-200"
                              title="Conciliar (gerar lançamento)">
                              Conciliar
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}
